import React, { useState, useEffect } from 'react';
import { ObjectModel, useObjectsTab } from '../items/hooks/HObjects';
import { useCompanies } from '../items/hooks/HCompanies';
import { User } from '../items/interfaces/IUsers';
import ObjectForm from '../dialogs/ObjectForm';
import ObjectDocsForm from '../dialogs/ObjectDocsForm';
import { StatusCircleIndicator, OccupancyGlassIndicator, ZoningIndicator } from '../utils/graphcomponents';
import { IconButton, Tooltip, Skeleton, Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';

const COUNTRY_NAME_TO_ISO: Record<string, string> = {
    italy: 'IT',
    switzerland: 'CH',
    germany: 'DE',
    france: 'FR',
    spain: 'ES',
    portugal: 'PT',
    netherlands: 'NL',
    belgium: 'BE',
    austria: 'AT',
    ireland: 'IE',
    luxembourg: 'LU',
    poland: 'PL',
    greece: 'GR',
    sweden: 'SE',
    norway: 'NO',
    denmark: 'DK',
    finland: 'FI',
    'united kingdom': 'GB',
    uk: 'GB',
    usa: 'US',
    'united states': 'US',
    'united states of america': 'US',
    canada: 'CA',
    australia: 'AU',
    japan: 'JP',
    singapore: 'SG',
    uae: 'AE',
    'united arab emirates': 'AE',
};

const isoToFlag = (isoCode: string) =>
    String.fromCodePoint(
        ...isoCode
            .toUpperCase()
            .split('')
            .map((char) => 127397 + char.charCodeAt(0))
    );

const getCountryDisplay = (rawCountry: unknown) => {
    const country = String(rawCountry ?? '').trim();
    if (!country) return { flag: '🏳️', label: '—' };

    const normalized = country.toLowerCase();
    const normalizedCode = normalized === 'uk' ? 'gb' : normalized;
    const isoCode = /^[a-z]{2}$/i.test(normalizedCode)
        ? normalizedCode.toUpperCase()
        : COUNTRY_NAME_TO_ISO[normalized] || '';

    if (!isoCode) return { flag: '🏳️', label: country };

    return { flag: isoToFlag(isoCode), label: country };
};

// Normalize various representations of the `approved` field to a boolean.
const isApproved = (v: any): boolean => {
    if (v === true || v === 1) return true;
    if (typeof v === 'string') {
        const s = v.trim().toLowerCase();
        if (s === '1' || s === 'true') return true;
        return false;
    }
    return false;
};

const CURRENCY_SYMBOLS: Record<string, string> = {
    EUR: 'EUR',
    USD: '$',
    GBP: 'GBP',
    CHF: 'CHF',
    AED: 'AED',
    JPY: 'JPY',
};

interface GObjectsTableProps {
    objects?: ObjectModel[];
    selectedRows?: boolean[];
    onToggleSelect?: (index: number) => void;
    onToggleSelectAll?: (select: boolean) => void;
    onEdit?: (object: ObjectModel, updatedData: Partial<ObjectModel>) => Promise<void>;
    onDelete?: (object: ObjectModel) => void;
    user?: User | null;
    selectableFlags?: boolean;
    selectedObjectIds?: Array<string | number>;
    onToggleObjectSelect?: (objectId: string | number) => void;
    onRowClick?: (object: ObjectModel) => void;
}

/**
 * GObjectsTable displays a table of objects with edit and delete actions.
 *
 * @param {GObjectsTableProps} props - Props for the table.
 * @returns {JSX.Element} The rendered table component.
 */
const GObjectsTable: React.FC<GObjectsTableProps> = ({
    objects,
    selectedRows = [],
    onToggleSelect,
    onToggleSelectAll,
    onEdit,
    onDelete,
    user,
    selectableFlags = false,
    selectedObjectIds = [],
    onToggleObjectSelect,
    onRowClick,
}) => {
    const { filteredObjects, fetchObjectMainImage, createObjectMedia, approveObject, loadAllObjects } = useObjectsTab();
    const { fetchCompanyMainImage } = useCompanies();

    // role_id is not consistently typed across the app; accept both 1 and '1'.
    const baseIsAdmin = Number(user?.role_id) === 1;

    const userCompany = user?.company;
    const canEditObject = (obj: any) => baseIsAdmin || obj?.company === userCompany || obj?.owner === userCompany;
    

    const rows = objects ?? filteredObjects;

    useEffect(() => {
        // Consolidated debug log: print the union of property keys and sample values
        try {
            const keys = new Set<string>();
            for (const r of rows) {
                if (r && typeof r === 'object') {
                    Object.keys(r).forEach((k) => keys.add(k));
                }
            }
            const sortedKeys = Array.from(keys).sort();

            const samples = rows.slice(0, 10).map((r) => {
                const s: Record<string, any> = {};
                for (const k of sortedKeys) {
                    try {
                        s[k] = (r as any)[k];
                    } catch (err) {
                        s[k] = undefined;
                    }
                }
                return s;
            });

            console.log('[GObjectsTable] ObjectModel properties and sample values:', { keys: sortedKeys, samples });
        } catch (e) {
            // intentionally silent
        }
    }, [rows]);

    const formatPriceInMillions = (value: string | number | null | undefined) => {
        if (value === null || value === undefined || value === '') return '—';
        const numericValue = Number(value);
        if (Number.isNaN(numericValue)) return String(value);

        const millions = numericValue / 1_000_000;
        const decimals = Number.isInteger(millions) ? 0 : 1;
        return `${millions.toLocaleString('en-US', {
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals,
        })}M`;
    };

    const formatCurrencyPrice = (currency: unknown, price: unknown) => {
        const code = String(currency ?? '').trim().toUpperCase();
        const symbol = CURRENCY_SYMBOLS[code] || code || '';
        const formattedPrice = formatPriceInMillions(price as string | number | null | undefined);
        if (formattedPrice === '—') return '—';
        return symbol ? `${symbol} ${formattedPrice}` : formattedPrice;
    };

    
    const selectedObjectIdsSet = new Set(selectedObjectIds.map((id) => String(id)));

    // Docs dialog state
    const [docsDialogOpen, setDocsDialogOpen] = useState(false);
    const [selectedObjectId, setSelectedObjectId] = useState<string | number | null>(null);

    // Approve dialog state
    const [approveDialogOpen, setApproveDialogOpen] = useState(false);
    const [approveTargetObject, setApproveTargetObject] = useState<ObjectModel | null>(null);

    // Edit form state
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [editingObject, setEditingObject] = useState<ObjectModel | null>(null);

    // Main image state: map of object ID to main image URL
    const [mainImages, setMainImages] = useState<{ [key: string]: string }>({});
    const [mainImagesLoading, setMainImagesLoading] = useState(false);

    /**
     * Load main images for all visible objects
     */
    useEffect(() => {
        const loadMainImages = async () => {
            setMainImagesLoading(true);
            const imageMap: { [key: string]: string } = {};

            for (const object of rows) {
                const mainImage = await fetchObjectMainImage(object.id);
                if (mainImage?.url) {
                    imageMap[String(object.id)] = mainImage.url;
                    continue;
                }

                // If object doesn't have a main image, try company-level image
                try {
                    const companyImage = await fetchCompanyMainImage(object.company);
                    if (companyImage?.url) {
                        imageMap[String(object.id)] = companyImage.url;
                    }
                } catch (err) {
                    // ignore and continue
                }
            }

            setMainImages(imageMap);
            setMainImagesLoading(false);
        };

        loadMainImages();
    }, [rows, fetchObjectMainImage, fetchCompanyMainImage]);

    /**
     * Open docs dialog for an object
     */
    const handleViewDocs = (objectId: string | number) => {
        setSelectedObjectId(objectId);
        setDocsDialogOpen(true);
    };

    /**
     * Close docs dialog
     */
    const handleCloseDocsDialog = () => {
        setDocsDialogOpen(false);
        setSelectedObjectId(null);
    };

    const handleEditClick = (object: ObjectModel) => {
        setEditingObject(object);
        setEditDialogOpen(true);
    };

    const handleCloseEditDialog = () => {
        setEditDialogOpen(false);
        setEditingObject(null);
    };

    const handleEditSubmit = async (updatedData: Partial<ObjectModel>) => {
        if (!onEdit || !editingObject) return;
        await onEdit(editingObject, updatedData);
        handleCloseEditDialog();
    };

    return (
        <>
            <table className='elegant-table users-rounded-table no-borders'>
                <thead>
                    <tr>
                        {selectableFlags && <th className='gobjects-table-header-flag'>Flag</th>}
                        <th className='gobjects-table-header-deal'>Deal</th>
                        {/* Wider image column so pictures can display without excessive cropping */}
                        <th className='gobjects-table-header-image' style={{ width: 140 }}></th>
                        <th className='gobjects-table-header-location'>Location</th>
                        <th>Type</th>
                        <th className='gobjects-table-header-status'>Status</th>
                        <th className='gobjects-table-header-occupancy'>Occupancy</th>
                        <th className='gobjects-table-header-zoning'>Zoning</th>
                        <th className='gobjects-table-header-price'>Price</th>
                        <th>Yield</th>
                        <th className='gobjects-table-header-actions'>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((object, index) => (
                        <tr
                            key={object.id}
                            onClick={() => onRowClick?.(object)}
                            className={onRowClick ? 'gobjects-table-row-clickable' : 'gobjects-table-row-default'}
                        >
                            {selectableFlags && (
                                <td className='gobjects-cell-flag'>
                                    <span
                                        role='button'
                                        aria-label={`Select object ${object.id}`}
                                        onClick={() => onToggleObjectSelect?.(object.id)}
                                        className={`gobjects-flag-selector ${selectedObjectIdsSet.has(String(object.id)) ? 'selected' : 'unselected'}`}
                                    >
                                        {selectedObjectIdsSet.has(String(object.id)) ? '🚩' : '⚑'}
                                    </span>
                                </td>
                            )}
                            <td className='gobjects-cell-image' style={{ width: 140 }}>
                                {mainImagesLoading ? (
                                    <Skeleton variant='rectangular' width={120} height={90} sx={{ borderRadius: '6px', mx: 'auto' }} />
                                ) : mainImages[String(object.id)] ? (
                                    <img
                                        src={mainImages[String(object.id)]}
                                        alt={object.name}
                                        className='gobjects-table-image'
                                        style={{ width: '120px', height: '90px', objectFit: 'contain', borderRadius: '6px', display: 'block', margin: '0 auto', backgroundColor: '#f5f5f5' }}
                                    />
                                ) : (
                                    <Skeleton variant='rectangular' width={120} height={90} sx={{ borderRadius: '6px', mx: 'auto', bgcolor: 'grey.100' }} animation={false} />
                                )}
                            </td>
                            <td>
                                <span className='gobjects-cell-name'>{object.name || '—'}</span>
                            </td>
                            <td>
                                {(() => {
                                    const location = getCountryDisplay(object.country);
                                    return (
                                        <div className='gobjects-location-display'>
                                            <span className='gobjects-location-flag' aria-hidden='true'>
                                                {location.flag}
                                            </span>
                                            <span>{location.label}</span>
                                        </div>
                                    );
                                })()}
                            </td>
                            <td>{object.type_ || '—'}</td>
                            <td className='gobjects-cell-status'>
                                <StatusCircleIndicator status={object.status as string | null | undefined} />
                            </td>
                            <td className='gobjects-cell-occupancy'>
                                <OccupancyGlassIndicator occupancy={object.occupancy as string | null | undefined} />
                            </td>
                            <td className='gobjects-cell-zoning'>
                                <ZoningIndicator zoning={object.zoning as string | null | undefined} />
                            </td>
                            <td>{formatCurrencyPrice(object.currency, object.price)}</td>
                            <td>
                                {object.yield_net ? `${Number(object.yield_net).toFixed(2)}%` : '—'}
                            </td>
                            <td className='gobjects-cell-actions'>
                                <div className='gobjects-actions-container'>
                                    {canEditObject(object) && onEdit && (
                                        <Tooltip title='Edit'>
                                            <IconButton
                                                size='small'
                                                color='primary'
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleEditClick(object);
                                                }}
                                            >
                                                <EditIcon fontSize='small' />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                       {canEditObject(object) && onDelete && (
                                        <Tooltip title='Documents'>
                                        <IconButton
                                            size='small'
                                            color='primary'
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleViewDocs(object.id);
                                            }}
                                        >
                                            <FolderOpenIcon fontSize='small' />
                                        </IconButton>
                                    </Tooltip>
                                       )}
                                       {canEditObject(object) && onDelete && (
                                        <Tooltip title='Delete'>
                                                <IconButton
                                                    size='small'
                                                    color='error'
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        onDelete(object);
                                                    }}
                                                >
                                                    <DeleteOutlineIcon fontSize='small' />
                                                </IconButton>
                                            </Tooltip>
                                        )}  
                                    {baseIsAdmin && onDelete && (
                                        <>
                                            <Tooltip title={isApproved(object.approved) ? 'Revoke approval' : 'Approve'}>
                                                <IconButton
                                                    size='small'
                                                    color={isApproved(object.approved) ? 'warning' : 'success'}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        // store a normalized boolean for the dialog
                                                        setApproveTargetObject({ ...object, approved: isApproved(object.approved) });
                                                        setApproveDialogOpen(true);
                                                    }}
                                                >
                                                    {isApproved(object.approved) ? (
                                                        <CancelIcon fontSize='small' />
                                                    ) : (
                                                        <CheckCircleIcon fontSize='small' />
                                                    )}
                                                </IconButton>
                                            </Tooltip>

                                        </>
                                    )}
                                </div>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>

            {/* Docs Dialog */}
            <ObjectDocsForm
                open={docsDialogOpen}
                onClose={handleCloseDocsDialog}
                objectId={selectedObjectId}
                canUpload={(() => {
                    if (baseIsAdmin) return true;
                    const selectedObj = rows.find((o) => String(o.id) === String(selectedObjectId));
                    return Boolean(selectedObj && canEditObject(selectedObj));
                })()}
                    onFileDrop={async (file, objectId) => {
                    await createObjectMedia(objectId, file);
                }}
            />

            {/* Edit Object Form */}
            {(((editingObject && canEditObject(editingObject)) || baseIsAdmin) && onEdit) && (
                <ObjectForm
                    open={editDialogOpen}
                    onClose={handleCloseEditDialog}
                    onSubmit={handleEditSubmit}
                    defaultValues={editingObject || undefined}
                />
            )}

            {/* Approve Confirmation Dialog */}
            <Dialog open={approveDialogOpen} onClose={() => setApproveDialogOpen(false)}>
                <DialogTitle>{isApproved(approveTargetObject?.approved) ? 'Revoke approval' : 'Approve object'}</DialogTitle>
                <DialogContent>
                    <Typography>
                        {isApproved(approveTargetObject?.approved)
                            ? `Are you sure you want to revoke approval for "${approveTargetObject?.name || approveTargetObject?.id}"?`
                            : `Are you sure you want to mark "${approveTargetObject?.name || approveTargetObject?.id}" as approved?`}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setApproveDialogOpen(false)}>Cancel</Button>
                    <Button
                        variant='contained'
                        color={isApproved(approveTargetObject?.approved) ? 'secondary' : 'primary'}
                        onClick={async (e) => {
                            e.stopPropagation();
                            if (!approveTargetObject) return;
                            try {
                                // If currently approved, revoke (set to 0); otherwise approve (set to 1)
                                await approveObject(approveTargetObject.id, !isApproved(approveTargetObject.approved));

                                // Refresh objects from server to ensure table is up-to-date
                                try {
                                    await loadAllObjects();
                                } catch (reloadErr) {
                                    // non-fatal: log and continue
                                    console.error('Failed to reload objects after approve action', reloadErr);
                                }
                            } catch (err) {
                                console.error('Approve/Revoke action failed', err);
                            } finally {
                                setApproveDialogOpen(false);
                                setApproveTargetObject(null);
                            }
                        }}
                    >
                        {isApproved(approveTargetObject?.approved) ? 'Revoke' : 'Approve'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default GObjectsTable;
